import type { RideAudioEventType } from './types.ts';

export interface ProceduralCueSpec {
  readonly durationSeconds: number;
  readonly startFrequency: number;
  readonly endFrequency: number;
  readonly noiseMix: number;
  readonly gain: number;
  readonly category: 'ride' | 'ui';
  readonly cooldownSeconds: number;
}

const DEFAULT_CUE: ProceduralCueSpec = {
  durationSeconds: 0.18,
  startFrequency: 280,
  endFrequency: 220,
  noiseMix: 0.08,
  gain: 0.25,
  category: 'ride',
  cooldownSeconds: 0.08,
};

const CUE_SPECS: Readonly<Record<RideAudioEventType, ProceduralCueSpec>> = {
  station_hold: { ...DEFAULT_CUE, durationSeconds: 0.1, startFrequency: 170, gain: 0.12 },
  station_dispatch: {
    ...DEFAULT_CUE,
    durationSeconds: 0.24,
    startFrequency: 260,
    endFrequency: 520,
    gain: 0.3,
  },
  station_arrival: {
    ...DEFAULT_CUE,
    durationSeconds: 0.3,
    startFrequency: 340,
    endFrequency: 180,
    noiseMix: 0.18,
  },
  ride_complete: {
    ...DEFAULT_CUE,
    durationSeconds: 0.5,
    startFrequency: 420,
    endFrequency: 760,
    gain: 0.34,
    cooldownSeconds: 0.5,
  },
  lift_enter: {
    ...DEFAULT_CUE,
    durationSeconds: 0.16,
    startFrequency: 120,
    endFrequency: 180,
    noiseMix: 0.25,
  },
  lift_exit: { ...DEFAULT_CUE, startFrequency: 210, endFrequency: 300 },
  launch_enter: {
    ...DEFAULT_CUE,
    durationSeconds: 0.36,
    startFrequency: 90,
    endFrequency: 620,
    noiseMix: 0.2,
    gain: 0.34,
  },
  launch_exit: { ...DEFAULT_CUE, startFrequency: 520, endFrequency: 260 },
  brake_enter: {
    ...DEFAULT_CUE,
    durationSeconds: 0.32,
    startFrequency: 780,
    endFrequency: 160,
    noiseMix: 0.45,
    gain: 0.3,
  },
  brake_exit: { ...DEFAULT_CUE, startFrequency: 190, endFrequency: 140, gain: 0.16 },
  stall: {
    ...DEFAULT_CUE,
    durationSeconds: 0.46,
    startFrequency: 190,
    endFrequency: 70,
    gain: 0.32,
    cooldownSeconds: 0.75,
  },
  intensity_warning: {
    ...DEFAULT_CUE,
    durationSeconds: 0.2,
    startFrequency: 420,
    endFrequency: 340,
    cooldownSeconds: 0.35,
  },
  intensity_severe: {
    ...DEFAULT_CUE,
    durationSeconds: 0.28,
    startFrequency: 330,
    endFrequency: 220,
    gain: 0.32,
    cooldownSeconds: 0.5,
  },
  intensity_block: {
    ...DEFAULT_CUE,
    durationSeconds: 0.42,
    startFrequency: 240,
    endFrequency: 80,
    gain: 0.36,
    cooldownSeconds: 0.75,
  },
  photo_marker: {
    ...DEFAULT_CUE,
    durationSeconds: 0.12,
    startFrequency: 900,
    endFrequency: 1300,
    noiseMix: 0.35,
    gain: 0.28,
    cooldownSeconds: 0.3,
  },
  joint_clack: {
    ...DEFAULT_CUE,
    durationSeconds: 0.055,
    startFrequency: 260,
    endFrequency: 140,
    noiseMix: 0.7,
    gain: 0.14,
    cooldownSeconds: 0.035,
  },
  ui_confirm: {
    ...DEFAULT_CUE,
    durationSeconds: 0.1,
    startFrequency: 540,
    endFrequency: 720,
    gain: 0.2,
    category: 'ui',
  },
  ui_general_click: {
    ...DEFAULT_CUE,
    durationSeconds: 0.09,
    startFrequency: 480,
    endFrequency: 520,
    gain: 0.16,
    category: 'ui',
  },
  ui_build_select: {
    ...DEFAULT_CUE,
    durationSeconds: 0.12,
    startFrequency: 420,
    endFrequency: 610,
    gain: 0.18,
    category: 'ui',
  },
  track_place_success: {
    ...DEFAULT_CUE,
    durationSeconds: 0.12,
    startFrequency: 190,
    endFrequency: 150,
    noiseMix: 0.28,
    gain: 0.2,
    category: 'ui',
  },
  ui_undo_delete: {
    ...DEFAULT_CUE,
    durationSeconds: 0.12,
    startFrequency: 520,
    endFrequency: 300,
    gain: 0.18,
    category: 'ui',
  },
  ui_publish_success: {
    ...DEFAULT_CUE,
    durationSeconds: 0.34,
    startFrequency: 420,
    endFrequency: 760,
    gain: 0.24,
    category: 'ui',
  },
  ui_system_failure: {
    ...DEFAULT_CUE,
    durationSeconds: 0.28,
    startFrequency: 320,
    endFrequency: 160,
    gain: 0.22,
    category: 'ui',
  },
};

export function getProceduralCueSpec(type: RideAudioEventType): ProceduralCueSpec {
  return CUE_SPECS[type];
}

function hashSeed(value: string): number {
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function nextNoise(state: { value: number }): number {
  state.value = (Math.imul(state.value, 1664525) + 1013904223) >>> 0;
  return (state.value / 0xffffffff) * 2 - 1;
}

export function createProceduralNoiseLoop(context: AudioContext, durationSeconds = 2): AudioBuffer {
  const length = Math.max(1, Math.round(context.sampleRate * durationSeconds));
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const samples = buffer.getChannelData(0);
  const random = { value: hashSeed('buncha-coasters-wind-v1') };
  let previous = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const white = nextNoise(random);
    previous = previous * 0.72 + white * 0.28;
    samples[index] = previous * 0.82 + white * 0.18;
  }
  return buffer;
}

export function createProceduralCueBuffer(
  context: AudioContext,
  type: RideAudioEventType,
): AudioBuffer {
  const spec = getProceduralCueSpec(type);
  const length = Math.max(1, Math.round(context.sampleRate * spec.durationSeconds));
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const samples = buffer.getChannelData(0);
  const random = { value: hashSeed(`buncha-coasters-${type}-v1`) };
  let phase = 0;

  for (let index = 0; index < samples.length; index += 1) {
    const progress = index / Math.max(1, samples.length - 1);
    const frequency = spec.startFrequency + (spec.endFrequency - spec.startFrequency) * progress;
    phase += (Math.PI * 2 * frequency) / context.sampleRate;
    const attack = Math.min(1, progress / 0.06);
    const decay = Math.pow(1 - progress, 1.6);
    const envelope = attack * decay;
    const tone = Math.sin(phase) * (1 - spec.noiseMix);
    const noise = nextNoise(random) * spec.noiseMix;
    samples[index] = (tone + noise) * envelope;
  }

  return buffer;
}

/**
 * Internal QA-only builder loop. It is intentionally generated at runtime and
 * is never eligible for the production registry or media export.
 */
export function createInternalBuilderTestLoop(
  context: AudioContext,
  durationSeconds = 8,
): AudioBuffer {
  const length = Math.max(1, Math.round(context.sampleRate * durationSeconds));
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const samples = buffer.getChannelData(0);
  const notes = [261.63, 329.63, 392, 493.88];
  for (let index = 0; index < samples.length; index += 1) {
    const time = index / context.sampleRate;
    const beat = time * 2;
    const note = notes[Math.floor(beat) % notes.length];
    const phase = (time * note * Math.PI * 2) % (Math.PI * 2);
    const pluck = Math.exp(-((beat % 1) * 5.5));
    const fadeIn = time >= 0.08 ? 1 : Math.sin((Math.PI * 0.5 * time) / 0.08);
    const remaining = durationSeconds - time;
    const fadeOut = remaining >= 0.08 ? 1 : Math.sin((Math.PI * 0.5 * remaining) / 0.08);
    const barFade = fadeIn * fadeOut;
    samples[index] = (Math.sin(phase) * 0.65 + Math.sin(phase * 2) * 0.18) * pluck * barFade * 0.2;
  }
  return buffer;
}
